import { useEffect, useRef } from "react"
import Intercom, { update as updateIntercom } from "@intercom/messenger-js-sdk"
import type { IntercomSettings } from "@intercom/messenger-js-sdk/dist/types"
import { useLocation } from "react-router-dom"
import { useMediaQuery } from "@/hooks/use-media-query"
import { isAbortError } from "@/lib/errors"
import { getIntercomBootUser } from "@/lib/intercom/api"
import { INTERCOM_SESSION_CHANGED_EVENT } from "@/lib/intercom/events"
import { getIntercomAppId, shutdownIntercomMessenger } from "@/lib/intercom/messenger"

const INTERCOM_TOKEN_REFRESH_SKEW_MS = 5 * 60 * 1000
const MIN_INTERCOM_TOKEN_REFRESH_DELAY_MS = 30 * 1000

export function useIntercom() {
  const { pathname } = useLocation()
  const shouldLoadSignedUser = shouldLoadIntercomSignedUser(pathname)
  // On a phone the floating launcher would sit on top of the client workspace's own controls (the chat composer's
  // send button), so it hides there; support stays one tap away in that sidebar's profile menu. Onboarding and other
  // screens without that menu keep the launcher, and desktop is unchanged.
  const isPhone = useMediaQuery("max-md")
  const hideLauncher = isPhone && pathname.startsWith("/app/clients/")
  const hideLauncherRef = useRef(hideLauncher)
  const hasBootedRef = useRef(false)

  // Follow the viewport without rebooting the messenger. Declared before the boot effect so a boot in the same
  // commit reads the current value; once booted, changes go through `update`.
  useEffect(() => {
    hideLauncherRef.current = hideLauncher
    if (hasBootedRef.current) {
      updateIntercom({ hide_default_launcher: hideLauncher })
    }
  }, [hideLauncher])

  useEffect(() => {
    const appId = getIntercomAppId()
    if (!appId) {
      return
    }

    let cancelled = false
    let hasBooted = false
    hasBootedRef.current = false
    let controller: AbortController | null = null
    let refreshTimer: number | null = null

    const applyIntercomSettings = (settings: IntercomSettings) => {
      if (hasBooted) {
        updateIntercom(settings)
      } else {
        Intercom(settings)
        hasBooted = true
        hasBootedRef.current = true
      }
    }

    const clearRefreshTimer = () => {
      if (refreshTimer) {
        window.clearTimeout(refreshTimer)
        refreshTimer = null
      }
    }

    const scheduleSignedUserRefresh = (expiresAt: number) => {
      clearRefreshTimer()
      refreshTimer = window.setTimeout(refreshIntercom, getIntercomTokenRefreshDelay(expiresAt))
    }

    const bootIntercom = async () => {
      clearRefreshTimer()
      if (!shouldLoadSignedUser) {
        bootAnonymousIntercom(appId, hideLauncherRef.current)
        hasBooted = true
        hasBootedRef.current = true
        return
      }

      controller?.abort()
      controller = new AbortController()
      const currentController = controller
      const user = await getIntercomBootUser(currentController.signal)

      if (!cancelled && controller === currentController) {
        if (user) {
          const { expires_at: expiresAt, ...settings } = user
          applyIntercomSettings({ ...getIntercomBaseSettings(appId, hideLauncherRef.current), ...settings })
          scheduleSignedUserRefresh(expiresAt)
        } else {
          bootAnonymousIntercom(appId, hideLauncherRef.current)
          hasBooted = true
          hasBootedRef.current = true
        }
      }
    }

    const refreshIntercom = () => {
      void bootIntercom().catch((error) => {
        if (!cancelled && !isAbortError(error)) {
          bootAnonymousIntercom(appId, hideLauncherRef.current)
          hasBooted = true
          hasBootedRef.current = true
        }
      })
    }

    refreshIntercom()
    window.addEventListener(INTERCOM_SESSION_CHANGED_EVENT, refreshIntercom)

    return () => {
      cancelled = true
      window.removeEventListener(INTERCOM_SESSION_CHANGED_EVENT, refreshIntercom)
      clearRefreshTimer()
      controller?.abort()
      shutdownIntercomMessenger()
      hasBootedRef.current = false
    }
  }, [shouldLoadSignedUser])
}

function bootAnonymousIntercom(appId: string, hideLauncher: boolean) {
  shutdownIntercomMessenger()
  Intercom(getIntercomBaseSettings(appId, hideLauncher))
}

function getIntercomBaseSettings(appId: string, hideLauncher: boolean) {
  return {
    app_id: appId,
    hide_default_launcher: hideLauncher,
  } satisfies IntercomSettings
}

function getIntercomTokenRefreshDelay(expiresAt: number) {
  const delay = expiresAt * 1000 - Date.now() - INTERCOM_TOKEN_REFRESH_SKEW_MS
  return Math.max(delay, MIN_INTERCOM_TOKEN_REFRESH_DELAY_MS)
}

function shouldLoadIntercomSignedUser(pathname: string) {
  return pathname === "/app" || pathname.startsWith("/app/") || pathname === "/auth/select-organisation"
}
